import { getReadableShiftCodeTextColor } from "@/utils/fontSizeUtils";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ArrowDown, ArrowRight, X } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import CustomDragNDropModal from "@/components/modal/CustomDragNDropModal";
import { cn } from "@/utils/cn";
import { DateUtils } from "@utils/dayUtill";
import { getScheduleShiftCodeFontSize } from "@utils/fontSizeUtils";
import type { EmergencyReplacementSlotPick } from "@/types/emergency-replacement.types";
import httpClient from "@/config/axios";
import { toast } from "@/components/toast/CustomToast";
import { Checkbox } from "@/components/ui/checkbox";
import type * as type from "@vTypes";
import { useShiftsQuery } from "@/hooks/roster-craete/useShiftsQuery";
import { useShiftLookupMaps } from "@/hooks/shift/useShiftLookupMaps";
import { resolveShiftFromScheduleCellFromMaps } from "@/utils/shift-util";
import BtnSuccess from "@/components/ui/button/btn-success";
import BtnCancel from "@/components/ui/button/btn-cancle";
import EmergencyReplacementFindButton from "@/components/feature/roster_create/EmergencyReplacementFindButton";
import ReplacementGradientFrame from "@/components/feature/roster_create/ReplacementGradientFrame";
import ShiftCode from "@/components/shift/ShiftCode";

/** 인력 범위 체크박스 3종 — 조합은 API `ranking_scope`로 변환 */
type RankingScopeCheckbox = "OFF_ONLY" | "VACATION_ONLY" | "ON_DUTY_ONLY";

/**
 * POST `/roster/replacement/recommend` — `options.ranking_scope`
 * (단일 + OR 조합 — 체크 2개일 때 해당 문자열 전송)
 */
type RankingScopePayload =
  | "ALL"
  | "OFF_ONLY"
  | "ON_DUTY_ONLY"
  | "VACATION_ONLY"
  | "ON_DUTY_OR_OFF"
  | "ON_DUTY_OR_VACATION"
  | "OFF_OR_VACATION";

/** 당일 1슬롯 vs 기간(벌크) — 기간 지정 시 안내·플레이스홀더 분기 */
type ReplacementPeriodMode = "sameDay" | "period";

const NON_ALL_SCOPES: RankingScopeCheckbox[] = [
  "OFF_ONLY",
  "VACATION_ONLY",
  "ON_DUTY_ONLY",
];

type RecommendRequestOptions = {
  ranking_scope?: RankingScopePayload;
  allow_non_off_candidates?: boolean;
  include_explanations?: boolean;
  max_candidate_scan?: number;
};

type EmergencyReplacementRecommendRequest = {
  schedule_id: string;
  mode: "SINGLE";
  target_nurse_id: string;
  /** API 스펙: `shift_date` 아님 — 각 슬롯은 `date` 필수 */
  slots: Array<{
    date: string;
    shift: string;
  }>;
  /** 상위 후보 반환 개수 (기본 10) */
  top_k?: number;
  options?: RecommendRequestOptions;
};

type EmergencyReplacementBulkRequest = {
  schedule_id: string;
  mode: "BULK";
  target_nurse_id: string;
  /** 기간 지정: 서버가 이 범위 안의 근무 슬롯을 자동 생성 */
  absence_window: { start_date: string; end_date: string };
  top_k?: number;
  options?: RecommendRequestOptions;
};

/** BULK `results[]`: 슬롯(일자) 단위 후보 랭킹 */
type BulkSlotRecommendation = {
  date: string;
  shift: string;
  candidates: RecommendCandidate[];
};

/** BULK `bulk_paths[]`: 시나리오(경로) 단위 묶음 */
type BulkPathStep = {
  date: string;
  shift: string;
  candidate: RecommendCandidate;
};

/** BULK `bulk_paths[].violations` — 백엔드가 `total_count`로 집계를 주는 경우가 많음 */
type BulkPathViolationsPayload = {
  total_count?: number;
  by_type?: Record<string, number>;
  details?: unknown[];
};

type BulkPathRecommendation = {
  path_rank: number;
  scenario_label: string;
  steps: BulkPathStep[];
  total_path_score?: number;
  violations?: BulkPathViolationsPayload | null;
  /**
   * 이 시나리오로 일괄 적용 가능한지 (step 후보가 전부 유효할 때 true).
   * 서버가 `has_applicable_candidates`를 주면 그대로 쓰고, 없으면 steps로 계산.
   */
  hasApplicableCandidates: boolean;
};

/**
 * POST `/roster/replacement/recommend` — axios 인터셉터가 `response.data`만 반환하므로
 * 이 타입이 mutation `data`와 일치한다.
 */
type ReplacementRecommendPostBody =
  | RecommendCandidate[]
  | {
      schedule_id?: string;
      mode?: string;
      target_nurse_id?: string;
      results?: unknown[];
      bulk_paths?: unknown[];
      metadata?: Record<string, unknown>;
    };

/** `/roster/replacement/recommend` → `results[].candidates[]` */
type RecommendCandidate = {
  nurse_id: string;
  name: string;
  candidate_grade?: number | null;
  current_assigned_shift_code?: string;
  current_assigned_shift_pk_id?: string;
  final_score?: number;
  rank?: number;
  tags?: unknown;
  breakdown?: Record<string, unknown>;
  /** true면 실제 대체 인력 없음 — 적용 버튼 비활성 */
  no_candidate?: boolean;
};

function logEmergencyReplacementDebug(
  phase: "request" | "response",
  payload: unknown
) {
  if (!import.meta.env.DEV) return;
  console.info(`[resignation-debug] POST /roster/replacement/recommend ${phase}`, payload);
}

type ShiftColorItem = {
  shift_id: string;
  default_shift?: string | null;
  color?: string | null;
};

function isPlainRecord(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === "object" && !Array.isArray(v);
}

/**
 * POST /roster/replacement/recommend 응답 본문 정규화.
 * MCP/백엔드에 따라 `{ data: { results, bulk_paths, metadata } }` 등으로 한 겹 감쌀 수 있음.
 */
function unwrapReplacementRecommendBody(
  raw: unknown
): Record<string, unknown> | null {
  if (!isPlainRecord(raw)) return null;
  let cur: Record<string, unknown> = raw;
  for (let depth = 0; depth < 6; depth++) {
    const hasSlotPayload =
      Array.isArray(cur.results) ||
      Array.isArray(cur.slot_results) ||
      Array.isArray(cur.markov_results) ||
      Array.isArray(cur.bulk_paths) ||
      Array.isArray((cur as { bulkPaths?: unknown }).bulkPaths) ||
      cur.bulk_paths === null;
    if (hasSlotPayload) return cur;

    const next =
      (isPlainRecord(cur.data) ? cur.data : null) ??
      (isPlainRecord(cur.payload) ? cur.payload : null) ??
      (isPlainRecord(cur.result) ? cur.result : null) ??
      (isPlainRecord(cur.body) ? cur.body : null) ??
      (isPlainRecord(cur.response) ? cur.response : null);
    if (!next) return cur;
    cur = next;
  }
  return cur;
}

/** 슬롯 행에서 date/shift — API별 필드명·중첩(slot) 대응 */
function pickBulkSlotRowKeys(row: Record<string, unknown>): {
  date: string;
  shift: string;
} {
  const nested =
    (isPlainRecord(row.slot) ? row.slot : null) ??
    (isPlainRecord(row.replacement_slot) ? row.replacement_slot : null) ??
    (isPlainRecord(row.replacementSlot) ? row.replacementSlot : null);
  const date = String(
    row.date ??
      row.shift_date ??
      row.slot_date ??
      nested?.date ??
      nested?.shift_date ??
      ""
  ).slice(0, 10);
  const shift = String(
    row.shift ??
      row.shift_code ??
      row.shift_id ??
      nested?.shift ??
      nested?.shift_code ??
      ""
  );
  return { date, shift };
}

function coerceRecommendCandidate(raw: unknown): RecommendCandidate | null {
  if (!isPlainRecord(raw)) return null;
  if (raw.nurse_id == null && raw.name == null) return null;
  return raw as RecommendCandidate;
}

/** bulk_paths[].steps[] 한 건 — candidate 중첩·평탄화 모두 수용 */
function parseBulkPathStep(step: Record<string, unknown>): BulkPathStep | null {
  const candRaw =
    step.candidate ??
    step.selected_candidate ??
    step.chosen_candidate ??
    step.replacement_candidate;
  let candidate = coerceRecommendCandidate(candRaw);
  if (!candidate && step.nurse_id != null) {
    candidate = {
      nurse_id: String(step.nurse_id),
      name: String(step.nurse_name ?? step.candidate_name ?? step.name ?? ""),
    };
  }
  if (!candidate) return null;
  const { date, shift } = pickBulkSlotRowKeys(step);
  return { date, shift, candidate };
}

/** bulk_paths[].violations — 배열·숫자·`total_count`·레거시 `count` 등 변형 대응 */
function countBulkPathViolations(violations: unknown): number {
  if (violations == null) return 0;
  if (typeof violations === "number" && Number.isFinite(violations)) {
    return Math.max(0, violations);
  }
  if (Array.isArray(violations)) return violations.length;
  if (isPlainRecord(violations)) {
    const o = violations;
    if (typeof o.total_count === "number" && Number.isFinite(o.total_count)) {
      return Math.max(0, o.total_count);
    }
    if (typeof o.count === "number") return o.count;
    if (Array.isArray(o.items)) return o.items.length;
    if (Array.isArray(o.violations)) return o.violations.length;
    if (Array.isArray(o.messages)) return o.messages.length;
    if (Array.isArray(o.details)) return o.details.length;
    return Object.keys(o).length;
  }
  return 0;
}

const KOREAN_WEEKDAY_LABELS = [
  "\uc77c",
  "\uc6d4",
  "\ud654",
  "\uc218",
  "\ubaa9",
  "\uae08",
  "\ud1a0",
];

function formatEmergencyDateLabel(dateISO: string | null | undefined): string {
  if (!dateISO) return "";
  const d = DateUtils.parseKoreanDate(dateISO);
  if (!d.isValid()) return "";
  return `${d.year()}\ub144 ${d.month() + 1}\uc6d4 ${d.date()}\uc77c(${KOREAN_WEEKDAY_LABELS[d.day()]})`;
}

function formatEmergencyPeriodLabel(
  startISO: string | null | undefined,
  endISO: string | null | undefined
): string {
  if (!startISO) return "";
  const start = DateUtils.parseKoreanDate(startISO);
  const end = endISO ? DateUtils.parseKoreanDate(endISO) : null;
  if (!start.isValid()) return "";

  const startText = `${start.year()}\ub144 ${start.month() + 1}\uc6d4 ${start.date()}\uc77c(${KOREAN_WEEKDAY_LABELS[start.day()]})`;
  if (!end || !end.isValid()) return `${startText} ~ \uc885\ub8cc\uc77c`;

  const endDayText = `${end.date()}\uc77c(${KOREAN_WEEKDAY_LABELS[end.day()]})`;
  if (start.year() === end.year() && start.month() === end.month()) {
    return `${startText} ~ ${endDayText}`;
  }

  const endText = `${end.year()}\ub144 ${end.month() + 1}\uc6d4 ${endDayText}`;
  return `${startText} ~ ${endText}`;
}

function EmergencySelectionSummaryCard({
  nurseName,
  dateText,
  shiftLabel,
  shiftColor,
  muted = false,
}: {
  nurseName: string;
  dateText: string;
  shiftLabel?: string;
  shiftColor?: string;
  muted?: boolean;
}) {
  const hasShift = !!shiftLabel?.trim();

  return (
    <div
      className={cn(
        "flex h-[70px] w-full shrink-0 flex-col items-center justify-center gap-1 rounded-[10px] border border-[#D6D6DE] bg-white px-3 text-center",
        muted && "text-[#A8A8B3]"
      )}
    >
      <div className="flex min-w-0 items-center justify-center gap-2">
        {hasShift && <ShiftCode code={shiftLabel ?? ""} color={shiftColor} />}
        <p
          className={cn(
            "min-w-0 truncate text-[17px] font-semibold leading-tight "
          )}
        >
          {nurseName}
        </p>
      </div>
      {dateText && (
        <p
          className={cn(
            "truncate text-sm font-semibold mt-1 leading-tight text-muted-foreground"
          )}
        >
          {dateText}
        </p>
      )}
    </div>
  );
}

/** BULK step `date` → 근무표 `dayIndex`(0-based, 해당 월의 일 열) */
function bulkDateISOToDayIndex(dateISO: string): number | null {
  const d = DateUtils.parseKoreanDate(String(dateISO).slice(0, 10));
  if (!d.isValid()) return null;
  const dom = d.date();
  if (dom < 1 || dom > 31) return null;
  return dom - 1;
}

function normalizeRecommendTags(tags: unknown): string[] {
  if (!Array.isArray(tags)) return [];
  return tags.filter((t): t is string => typeof t === "string" && t.length > 0);
}

/** API 플래그·빈 id·태그로 ‘후보 없음’ 행 판별 — 적용(설정완료/기간 적용) 불가 */
function isRecommendNoCandidateRow(c: RecommendCandidate): boolean {
  if (c.no_candidate === true) return true;
  if (String(c.nurse_id ?? "").trim() === "") return true;
  const tags = normalizeRecommendTags(c.tags);
  if (tags.includes("no_candidate_available")) return true;
  return false;
}

/** BULK 시나리오 경로 — 모든 step에 실제 적용 가능한 후보가 있는지 */
function bulkPathStepsAreApplicable(steps: BulkPathStep[]): boolean {
  if (steps.length === 0) return false;
  for (const step of steps) {
    const cand = step.candidate;
    if (!cand || isRecommendNoCandidateRow(cand)) return false;
    if (!String(step.shift ?? "").trim()) return false;
    if (bulkDateISOToDayIndex(step.date) == null) return false;
  }
  return true;
}

/** `/roster/replacement/recommend` 후보 tags — 짧은 UI 문구 + 긍정/부정 */
const RECOMMEND_TAG_UI = {
  off_today: { label: "오늘 휴무", tone: "positive" as const },
  off_rest_candidate: { label: "휴무 대체 가능", tone: "positive" as const },
  off_day_shortage: {
    label: "월 OFF 부족",
    tone: "negative" as const,
  },
  same_grade: { label: "동일 연차/등급", tone: "positive" as const },
  near_grade: { label: "유사 연차/등급", tone: "positive" as const },
  high_preference: { label: "선호도 높음", tone: "positive" as const },
  high_pair_fit: { label: "페어 적합도 높음", tone: "positive" as const },
  vacation_candidate: { label: "휴가자 후보", tone: "negative" as const },
  vacation_penalized: { label: "휴가 보호 감점", tone: "negative" as const },
  requires_reassignment: {
    label: "기존 근무 변경 필요",
    tone: "negative" as const,
  },
  consecutive_work_risk: { label: "연속근무 위험", tone: "negative" as const },
  night_to_day_evening_risk: {
    label: "야간 후 주/저녁 위험",
    tone: "negative" as const,
  },
  eve_to_day_risk: { label: "저녁 후 주간 위험", tone: "negative" as const },
  lookahead_eve_to_day_risk: {
    label: "익일 저녁→주간 위험",
    tone: "negative" as const,
  },
  lookahead_night_to_day_risk: {
    label: "익일 야간→주간 위험",
    tone: "negative" as const,
  },
  lookahead_night_to_evening_risk: {
    label: "익일 야간→저녁 위험",
    tone: "negative" as const,
  },
  monthly_night_limit_risk: {
    label: "월 야간한도 위험",
    tone: "negative" as const,
  },
  night_15n_hard_limit: {
    label: "월 야간 15회 초과",
    tone: "negative" as const,
  },
  protected_off_2n2o_day1_risk: {
    label: "2N2O 1일차 보호위험",
    tone: "negative" as const,
  },
  protected_off_2n2o_day2_risk: {
    label: "2N2O 2일차 보호위험",
    tone: "negative" as const,
  },
  protected_off_3n2o_day1_risk: {
    label: "3N2O 1일차 보호위험",
    tone: "negative" as const,
  },
  protected_off_3n2o_day2_risk: {
    label: "3N2O 2일차 보호위험",
    tone: "negative" as const,
  },
  shift_capability_fallback: {
    label: "근무유형 불일치",
    tone: "negative" as const,
  },
  no_candidate_available: {
    label: "후보 없음",
    tone: "negative" as const,
  },
  fallback_target_shift_mapping: {
    label: "대상근무 코드 보정",
    tone: "negative" as const,
  },
  fallback_shift_mapping: {
    label: "근무 코드 보정",
    tone: "negative" as const,
  },
} satisfies Record<string, { label: string; tone: "positive" | "negative" }>;

type RecommendTagTone = "positive" | "negative" | "neutral";

function resolveRecommendTagKey(raw: string): string {
  const t = raw.trim();
  const colon = t.indexOf(":");
  const eq = t.indexOf("=");
  const cut =
    colon !== -1 && (eq === -1 || colon < eq) ? colon : eq !== -1 ? eq : -1;
  const head = cut === -1 ? t : t.slice(0, cut).trim();
  return head || t;
}

function getRecommendTagUi(raw: string): {
  label: string;
  tone: RecommendTagTone;
} {
  const key = resolveRecommendTagKey(raw);
  const mapped = RECOMMEND_TAG_UI[key as keyof typeof RECOMMEND_TAG_UI];
  if (mapped) return { label: mapped.label, tone: mapped.tone };
  /** `off_day_shortage_8of9` 등 `off_day_shortage` 접두 변형 태그 */
  if (key !== "off_day_shortage" && key.startsWith("off_day_shortage")) {
    const o = RECOMMEND_TAG_UI.off_day_shortage;
    return { label: o.label, tone: o.tone };
  }
  if (/_risk$/i.test(key)) return { label: raw.trim(), tone: "negative" };
  return { label: raw.trim(), tone: "neutral" };
}

/** `shift_capability_fallback*` 는 항상 맨 앞 (서버에서 중요한 특이 케이스) */
function isShiftCapabilityFallbackTag(raw: string): boolean {
  const key = resolveRecommendTagKey(raw);
  return key.startsWith("shift_capability_fallback");
}

/** 표시 순서: `shift_capability_fallback*` 맨 앞 → 긍정·중립 → 부정 */
function sortRecommendTagsForDisplay(tags: string[]): string[] {
  const first: string[] = [];
  const front: string[] = [];
  const back: string[] = [];
  for (const t of tags) {
    if (isShiftCapabilityFallbackTag(t)) {
      first.push(t);
    } else if (getRecommendTagUi(t).tone === "negative") {
      back.push(t);
    } else {
      front.push(t);
    }
  }
  return [...first, ...front, ...back];
}

/** 추천태그 셀 */
function recommendTagInlineCn(raw: string, isSelected: boolean): string {
  if (isShiftCapabilityFallbackTag(raw)) {
    return cn(
      "inline-flex max-w-[min(100%,280px)] shrink-0 whitespace-normal break-words rounded-md border border-amber-400/90 bg-amber-50 px-2 py-0.5 text-[12px] leading-snug text-amber-950 shadow-sm",
      isSelected ? "font-semibold" : "font-medium"
    );
  }
  const ui = getRecommendTagUi(raw);
  return cn(
    "inline-flex shrink-0 py-0.5 whitespace-nowrap text-zinc-800",
    isSelected ? "font-semibold" : "font-medium",
    (ui.tone === "negative" ||
      /requires|vacation|risk|violation|warning/i.test(raw)) &&
      "text-red-600"
  );
}

export default function ModalEmergencyReplacement({
  open,
  onOpenChange,
  targetRef,
  portalZIndex = 10,
  hasWorkers = true,
  slotPick = null,
  scheduleId,
  groupId,
  availableShifts = [],
  shiftDefinitions,
  onApplyReplacement,
  /** 추천 리스트에서 선택한 대체 간호사 id — 테이블 해당일 셀 인디케이터용 */
  onReplacementCandidateChange,
  /** 기간 지정 모드에서 테이블 셀 클릭으로 선택된 범위 */
  bulkPeriodPick = null,
  /** 기간 스트립: 테이블과 동일 출처 — 대상 간호사 월 스케줄 + PK 매핑 */
  bulkPeriodTargetNurse = null,
  bulkPeriodCalendar = null,
  onBulkPathStepsChange,
  /** SINGLE 추천 결과 수신/초기화 시 부모에 알림 (뒤 테이블 셀 클릭 차단 판단용) */
  onRecommendResultChange,
  /** BULK 추천 결과 수신/초기화 시 부모에 알림 (뒤 테이블 셀 클릭 차단 판단용) */
  onBulkResultChange,
  /** 뒤 테이블 confirm 승인 시 호출할 모달 리셋 함수를 훅 ref에 등록 */
  onBulkResultResetRegister,
}: {
  open: boolean;
  onOpenChange: (value: boolean) => void;
  targetRef: React.RefObject<HTMLElement | null>;
  portalZIndex?: number;
  hasWorkers?: boolean;
  slotPick?: EmergencyReplacementSlotPick | null;
  scheduleId?: string;
  groupId?: string;
  /** 근무표와 동일한 시프트 맵 — PK 기반 표시명 매핑용 */
  availableShifts?: type.availableShifts[];
  shiftDefinitions?: ShiftColorItem[];
  onApplyReplacement?: (args: {
    nurseId: string;
    targetNurseId: string;
    dayIndex: number;
    shiftCode: string;
  }) => void;
  onReplacementCandidateChange?: (nurseId: string | null) => void;
  onEmergencyReplacementPeriodModeChange?: (
    next: ReplacementPeriodMode,
    previous: ReplacementPeriodMode
  ) => void;
  bulkPeriodPick?:
    | import("@/types/emergency-replacement.types").EmergencyBulkPeriodPick
    | null;
  bulkPeriodTargetNurse?: {
    schedule?: (string | null)[] | null;
    schedule_ids?: (number | null)[] | null;
  } | null;
  bulkPeriodCalendar?: { year: number; month: number } | null;
  /** BULK: 시나리오 선택 시 steps 목록 전달 (테이블 하이라이트용) */
  onBulkPathStepsChange?: (steps: BulkPathStep[] | null) => void;
  onRecommendResultChange?: (hasResult: boolean) => void;
  onBulkResultChange?: (hasResult: boolean) => void;
  onBulkResultResetRegister?: React.MutableRefObject<(() => void) | null>;
}) {
  const [replacementPeriodMode, setReplacementPeriodMode] =
    useState<ReplacementPeriodMode>("sameDay");

  // 추천 검색 옵션 (검색 버튼 누르기 전 선택)
  const [rankingScopeChecks, setRankingScopeChecks] = useState<
    Set<RankingScopeCheckbox>
  >(new Set());

  /** 체크박스 → API `ranking_scope` (1개=그대로, 2개=OR 조합, 0·3개=ALL) */
  const rankingScope = useMemo<RankingScopePayload>(() => {
    const checked = Array.from(rankingScopeChecks);
    const n = checked.length;
    if (n === 0) return "ALL";
    if (n === NON_ALL_SCOPES.length) return "ALL";
    if (n === 1) return checked[0] as RankingScopePayload;

    const s = new Set(checked);
    if (s.has("OFF_ONLY") && s.has("VACATION_ONLY")) return "OFF_OR_VACATION";
    if (s.has("OFF_ONLY") && s.has("ON_DUTY_ONLY")) return "ON_DUTY_OR_OFF";
    if (s.has("VACATION_ONLY") && s.has("ON_DUTY_ONLY"))
      return "ON_DUTY_OR_VACATION";

    return "ALL";
  }, [rankingScopeChecks]);

  const isAllScopeChecked =
    rankingScopeChecks.size === 0 ||
    rankingScopeChecks.size === NON_ALL_SCOPES.length;

  const [allowNonOffCandidates, setAllowNonOffCandidates] = useState(true);

  const { data: shiftsData } = useShiftsQuery(groupId);
  const { lookupMaps: shiftLookupMaps } = useShiftLookupMaps(
    availableShifts,
    shiftsData ?? null
  );

  const stableInitialPosition = useMemo(
    () => (targetRef ? undefined : { x: "50%", y: "50%" }),
    [targetRef]
  );

  const canFindReplacement = hasWorkers && slotPick != null;
  const shiftLabel = slotPick?.shiftLabel ?? slotPick?.shiftCode ?? "";
  const selectedDateLine = useMemo(() => {
    return formatEmergencyDateLabel(slotPick?.dateISO);
  }, [slotPick?.dateISO]);

  const canRequestRecommendation =
    canFindReplacement && !!scheduleId && !!slotPick?.nurseId;

  /** 기간 지정: 근무자·범위가 모두 확정되었을 때만 추천 버튼 활성 */
  const canRequestPeriodRecommendation = useMemo(() => {
    return (
      !!scheduleId && !!bulkPeriodPick?.nurseId && !!bulkPeriodPick?.endDateISO
    );
  }, [scheduleId, bulkPeriodPick]);

  const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(
    null
  );
  /** BULK: 선택된 bulk_path의 path_rank */
  const [selectedBulkPathRank, setSelectedBulkPathRank] = useState<
    number | null
  >(null);
  const prevSlotKeyRef = useRef<string | null>(null);

  // SINGLE 당일 mutation
  const {
    mutate: requestRecommend,
    reset: resetRecommendMutation,
    data: recommendResponse,
    isPending: isRecommendPending,
  } = useMutation({
    mutationFn: async () => {
      if (!slotPick || !scheduleId) {
        throw new Error("필수 정보가 없습니다.");
      }
      const payload: EmergencyReplacementRecommendRequest = {
        schedule_id: scheduleId,
        mode: "SINGLE",
        target_nurse_id: slotPick.nurseId,
        slots: [{ date: slotPick.dateISO, shift: slotPick.shiftCode }],
        top_k: 10,
        options: {
          ranking_scope: rankingScope,
          allow_non_off_candidates: allowNonOffCandidates,
          include_explanations: true,
          max_candidate_scan: 300,
        },
      };
      const params = groupId ? { params: { group_id: groupId } } : undefined;
      logEmergencyReplacementDebug("request", {
        params: params?.params ?? null,
        payload,
        slotPick,
      });
      const response = await httpClient.post<ReplacementRecommendPostBody>(
        "/roster/replacement/recommend",
        payload,
        params
      );
      logEmergencyReplacementDebug("response", {
        mode: "SINGLE",
        request: payload,
        response,
      });
      return response;
    },
    onSuccess: () => {
      onRecommendResultChange?.(true);
    },
  });

  // BULK 기간 지정 mutation
  const {
    mutate: requestBulkRecommend,
    reset: resetBulkRecommendMutation,
    data: bulkRecommendResponse,
    isPending: isBulkRecommendPending,
  } = useMutation({
    mutationFn: async () => {
      if (
        !bulkPeriodPick?.nurseId ||
        !bulkPeriodPick?.endDateISO ||
        !scheduleId
      ) {
        throw new Error("기간 지정 필수 정보가 없습니다.");
      }
      const payload: EmergencyReplacementBulkRequest = {
        schedule_id: scheduleId,
        mode: "BULK",
        target_nurse_id: bulkPeriodPick.nurseId,
        absence_window: {
          start_date: bulkPeriodPick.startDateISO,
          end_date: bulkPeriodPick.endDateISO,
        },
        top_k: 10,
        options: {
          ranking_scope: rankingScope,
          allow_non_off_candidates: allowNonOffCandidates,
          include_explanations: true,
          max_candidate_scan: 300,
        },
      };
      const params = groupId ? { params: { group_id: groupId } } : undefined;
      logEmergencyReplacementDebug("request", {
        params: params?.params ?? null,
        payload,
        bulkPeriodPick,
      });
      const response = await httpClient.post<ReplacementRecommendPostBody>(
        "/roster/replacement/recommend",
        payload,
        params
      );
      logEmergencyReplacementDebug("response", {
        mode: "BULK",
        request: payload,
        response,
      });
      return response;
    },
    onSuccess: () => {
      onBulkResultChange?.(true);
    },
    onError: (error: Error) => {
      toast.error(error.message || "기간 대체 근무자 조회에 실패했습니다.");
    },
  });

  const recommendList = useMemo<RecommendCandidate[]>(() => {
    if (Array.isArray(recommendResponse)) {
      return recommendResponse.filter(
        (c): c is RecommendCandidate =>
          !!c &&
          typeof c === "object" &&
          !Array.isArray(c) &&
          typeof (c as { nurse_id?: unknown }).nurse_id !== "undefined" &&
          typeof (c as { name?: unknown }).name === "string"
      );
    }
    if (!recommendResponse || typeof recommendResponse !== "object") return [];

    const root =
      unwrapReplacementRecommendBody(recommendResponse) ??
      (recommendResponse as Record<string, unknown>);

    /** `/roster/replacement/recommend` 실제 응답: `{ results: [{ candidates: [...] }] }` */
    if (Array.isArray(root.results)) {
      const out: RecommendCandidate[] = [];
      for (const entry of root.results) {
        if (!entry || typeof entry !== "object" || Array.isArray(entry))
          continue;
        const candidates = (entry as { candidates?: unknown[] }).candidates;
        if (Array.isArray(candidates)) {
          out.push(
            ...candidates.filter(
              (c): c is RecommendCandidate =>
                !!c &&
                typeof c === "object" &&
                !Array.isArray(c) &&
                typeof (c as { nurse_id?: unknown }).nurse_id !== "undefined" &&
                typeof (c as { name?: unknown }).name === "string"
            )
          );
        }
      }
      if (out.length > 0) return out;
    }

    if (Array.isArray(root.items)) {
      return root.items.filter(
        (c): c is RecommendCandidate =>
          !!c &&
          typeof c === "object" &&
          !Array.isArray(c) &&
          typeof (c as { nurse_id?: unknown }).nurse_id !== "undefined" &&
          typeof (c as { name?: unknown }).name === "string"
      );
    }
    if (Array.isArray(root.data)) {
      return root.data.filter(
        (c): c is RecommendCandidate =>
          !!c &&
          typeof c === "object" &&
          !Array.isArray(c) &&
          typeof (c as { nurse_id?: unknown }).nurse_id !== "undefined" &&
          typeof (c as { name?: unknown }).name === "string"
      );
    }
    return [];
  }, [recommendResponse]);
  const hasRecommendResponse = typeof recommendResponse !== "undefined";

  /** BULK 응답 파싱: results(슬롯별) + bulk_paths(시나리오별) */
  const { bulkSlotResults, bulkPaths } = useMemo<{
    bulkSlotResults: BulkSlotRecommendation[];
    bulkPaths: BulkPathRecommendation[];
  }>(() => {
    if (!bulkRecommendResponse || typeof bulkRecommendResponse !== "object") {
      return { bulkSlotResults: [], bulkPaths: [] };
    }
    const root =
      unwrapReplacementRecommendBody(bulkRecommendResponse) ??
      (bulkRecommendResponse as Record<string, unknown>);

    const rawResults: unknown[] = Array.isArray(root.results)
      ? root.results
      : Array.isArray(root.slot_results)
        ? root.slot_results
        : Array.isArray(root.markov_results)
          ? root.markov_results
          : [];

    const rawPaths: unknown[] = Array.isArray(root.bulk_paths)
      ? root.bulk_paths
      : Array.isArray((root as { bulkPaths?: unknown }).bulkPaths)
        ? ((root as { bulkPaths: unknown[] }).bulkPaths ?? [])
        : [];

    const parsedResults: BulkSlotRecommendation[] = rawResults
      .filter((r): r is Record<string, unknown> => isPlainRecord(r))
      .map((r) => {
        const { date, shift } = pickBulkSlotRowKeys(r);
        const rawCands =
          r.candidates ??
          r.recommendations ??
          r.top_candidates ??
          r.candidate_list;
        const candidates = Array.isArray(rawCands)
          ? rawCands.filter(
              (c): c is RecommendCandidate =>
                isPlainRecord(c) &&
                typeof (c as { nurse_id?: unknown }).nurse_id !== "undefined"
            )
          : [];
        return { date, shift, candidates };
      })
      .filter((slot) => slot.date.length > 0);

    const parsedPaths: BulkPathRecommendation[] = rawPaths
      .filter((p): p is Record<string, unknown> => isPlainRecord(p))
      .map((p) => {
        const rank = Number(
          p.path_rank ?? (p as { pathRank?: unknown }).pathRank ?? p.rank ?? 0
        );
        const label = String(
          p.scenario_label ??
            (p as { scenarioLabel?: unknown }).scenarioLabel ??
            p.label ??
            ""
        );
        const scoreRaw =
          p.total_path_score ??
          (p as { totalPathScore?: unknown }).totalPathScore ??
          p.path_score;
        const total_path_score =
          typeof scoreRaw === "number" && Number.isFinite(scoreRaw)
            ? scoreRaw
            : undefined;
        const rawSteps = Array.isArray(p.steps) ? p.steps : [];
        const steps: BulkPathStep[] = [];
        for (const s of rawSteps) {
          if (!isPlainRecord(s)) continue;
          const parsed = parseBulkPathStep(s);
          if (parsed) steps.push(parsed);
        }
        const violRaw = p.violations;
        const violationsParsed: BulkPathViolationsPayload | null =
          violRaw == null
            ? null
            : isPlainRecord(violRaw)
              ? (violRaw as BulkPathViolationsPayload)
              : null;

        const serverApplicable =
          (p as { has_applicable_candidates?: unknown })
            .has_applicable_candidates ??
          (p as { hasApplicableCandidates?: unknown }).hasApplicableCandidates;
        const hasApplicableCandidates =
          typeof serverApplicable === "boolean"
            ? serverApplicable
            : bulkPathStepsAreApplicable(steps);

        return {
          path_rank: rank,
          scenario_label: label,
          steps,
          total_path_score,
          violations: violationsParsed,
          hasApplicableCandidates,
        };
      })
      .filter(
        (path) => path.steps.length > 0 || path.scenario_label.length > 0
      );

    return { bulkSlotResults: parsedResults, bulkPaths: parsedPaths };
  }, [bulkRecommendResponse]);

  const hasBulkResponse = typeof bulkRecommendResponse !== "undefined";

  const selectedBulkPath = useMemo(
    () => bulkPaths.find((p) => p.path_rank === selectedBulkPathRank) ?? null,
    [bulkPaths, selectedBulkPathRank]
  );

  const sortedRecommendList = useMemo(() => {
    const list = [...recommendList];
    list.sort((a, b) => {
      const ra =
        typeof a.rank === "number" && Number.isFinite(a.rank) ? a.rank : 9999;
      const rb =
        typeof b.rank === "number" && Number.isFinite(b.rank) ? b.rank : 9999;
      if (ra !== rb) return ra - rb;
      const fa =
        typeof a.final_score === "number" && Number.isFinite(a.final_score)
          ? a.final_score
          : -Infinity;
      const fb =
        typeof b.final_score === "number" && Number.isFinite(b.final_score)
          ? b.final_score
          : -Infinity;
      return fb - fa;
    });
    return list;
  }, [recommendList]);

  const resetEmergencyModalState = useCallback(() => {
    resetRecommendMutation();
    resetBulkRecommendMutation();
    setSelectedCandidateId(null);
    setSelectedBulkPathRank(null);
    setRankingScopeChecks(new Set());
    setAllowNonOffCandidates(true);
    onRecommendResultChange?.(false);
    onBulkResultChange?.(false);
  }, [
    resetRecommendMutation,
    resetBulkRecommendMutation,
    onRecommendResultChange,
    onBulkResultChange,
  ]);

  /** 닫기/적용 직후 즉시 초기화 — useEffect만 쓰면 다음 틱까지 추천·선택 상태가 남아 설정완료가 유지될 수 있음 */
  const handleCloseModal = useCallback(() => {
    const picked = selectedCandidateId?.trim();
    if (recommendList.length > 0 && picked && picked.length > 0) {
      const ok = window.confirm(
        "대체 근무자를 선택한 상태입니다. 적용하지 않고 닫으면 근무자 대체가 취소됩니다.\n취소하고 닫으시겠습니까?"
      );
      if (!ok) return;
    }
    resetEmergencyModalState();
    onOpenChange(false);
  }, [
    onOpenChange,
    recommendList.length,
    resetEmergencyModalState,
    selectedCandidateId,
  ]);

  useEffect(() => {
    if (!open) {
      resetEmergencyModalState();
      setReplacementPeriodMode("sameDay");
    }
  }, [open, resetEmergencyModalState]);

  useEffect(() => {
    if (!open) return;
    setReplacementPeriodMode(bulkPeriodPick?.endDateISO ? "period" : "sameDay");
  }, [open, bulkPeriodPick?.endDateISO]);

  // 뒤 테이블 confirm 승인 시 호출할 리셋 함수 등록
  useEffect(() => {
    if (!onBulkResultResetRegister) return;
    onBulkResultResetRegister.current = resetBulkRecommendMutation;
    return () => {
      onBulkResultResetRegister.current = null;
    };
  }, [onBulkResultResetRegister, resetBulkRecommendMutation]);

  useEffect(() => {
    if (!open) return;

    const nextKey = slotPick
      ? `${slotPick.nurseId}|${slotPick.dayIndex}|${slotPick.shiftCode}|${slotPick.dateISO}`
      : null;
    const prevKey = prevSlotKeyRef.current;

    if (prevKey === null) {
      prevSlotKeyRef.current = nextKey;
      return;
    }

    if (prevKey !== nextKey && hasRecommendResponse) {
      resetEmergencyModalState();
    }

    prevSlotKeyRef.current = nextKey;
  }, [
    open,
    slotPick,
    hasRecommendResponse,
    resetEmergencyModalState,
  ]);

  useEffect(() => {
    if (!onReplacementCandidateChange) return;
    if (!open) {
      onReplacementCandidateChange(null);
      return;
    }
    const id = selectedCandidateId?.trim();
    onReplacementCandidateChange(id && id.length > 0 ? id : null);
  }, [open, selectedCandidateId, onReplacementCandidateChange]);

  useEffect(() => {
    setSelectedCandidateId(null);
  }, [slotPick?.nurseId, slotPick?.dayIndex, recommendResponse]);

  // BULK 시나리오 선택 → 테이블 steps 하이라이트 동기화
  // 콜백 참조가 매 렌더 바뀌면 무한 루프되므로 ref로 최신 함수만 호출
  const onBulkPathStepsChangeRef = useRef(onBulkPathStepsChange);
  onBulkPathStepsChangeRef.current = onBulkPathStepsChange;
  useEffect(() => {
    const cb = onBulkPathStepsChangeRef.current;
    if (!cb) return;
    if (!open) {
      cb(null);
      return;
    }
    cb(selectedBulkPath?.steps ?? null);
  }, [open, selectedBulkPath]);

  const selectedCandidate = useMemo(() => {
    if (selectedCandidateId == null) return null;
    return (
      recommendList.find((c) => String(c.nurse_id) === selectedCandidateId) ??
      null
    );
  }, [recommendList, selectedCandidateId]);

  const resolveShiftColor = useCallback(
    (code: string | null | undefined, fallback = "#A8A8B3") => {
      const trimmed = String(code ?? "").trim();
      if (!trimmed || !shiftDefinitions?.length) return fallback;

      const byShiftId = shiftDefinitions.find((s) => s.shift_id === trimmed);
      if (byShiftId?.color) return byShiftId.color;

      const byDefault = shiftDefinitions.find(
        (s) => s.default_shift === trimmed
      );
      if (byDefault?.color) return byDefault.color;

      // DD/EE/NN/MM/OO 같은 반복코드 fallback
      if (
        trimmed.length > 1 &&
        trimmed.split("").every((ch) => ch === trimmed[0])
      ) {
        const base = trimmed[0];
        const byBase = shiftDefinitions.find((s) => s.default_shift === base);
        if (byBase?.color) return byBase.color;
      }
      return fallback;
    },
    [shiftDefinitions]
  );

  /** BULK 기간 스트립: 우선 근무표 `schedule`+`schedule_ids`+lookupMaps (테이블과 동일), 없으면 API bulk 슬롯 */
  const bulkPeriodScheduleStrip = useMemo(() => {
    if (
      !bulkPeriodPick?.startDateISO ||
      !bulkPeriodPick?.endDateISO ||
      replacementPeriodMode !== "period"
    ) {
      return [];
    }
    const slotMap = new Map<string, string>();
    for (const sl of bulkSlotResults) {
      slotMap.set(sl.date.slice(0, 10), sl.shift);
    }
    const start = DateUtils.parseKoreanDate(
      bulkPeriodPick.startDateISO
    ).startOf("day");
    const end = DateUtils.parseKoreanDate(bulkPeriodPick.endDateISO).startOf(
      "day"
    );
    if (!start.isValid() || !end.isValid()) return [];
    const wdLabels = ["일", "월", "화", "수", "목", "금", "토"];
    const sched = bulkPeriodTargetNurse?.schedule;
    const ids = bulkPeriodTargetNurse?.schedule_ids;
    const calY = bulkPeriodCalendar?.year;
    const calM = bulkPeriodCalendar?.month;

    const out: Array<{
      iso: string;
      weekday: string;
      dayNum: number;
      dow: number;
      displayCode: string;
      color: string;
    }> = [];

    for (let d = start.clone(); !d.isAfter(end, "day"); d = d.add(1, "day")) {
      const iso = d.format("YYYY-MM-DD");
      const dow = d.day();
      const y = d.year();
      const m = d.month() + 1;
      const dom = d.date();
      const idx = dom - 1;

      let displayCode: string;
      let color: string;

      const useRosterRow =
        Array.isArray(sched) &&
        calY === y &&
        calM === m &&
        idx >= 0 &&
        idx < sched.length;

      if (useRosterRow) {
        const rawCell = sched[idx];
        const codeStr = rawCell == null ? "" : String(rawCell).trim();
        const idRaw = ids?.[idx];
        const numericId =
          idRaw != null && Number.isFinite(Number(idRaw))
            ? Number(idRaw)
            : null;

        if (!codeStr || codeStr === "-") {
          displayCode = "·";
          color = "#F0F0F2";
        } else {
          const resolved = resolveShiftFromScheduleCellFromMaps(
            codeStr,
            numericId,
            shiftLookupMaps
          );
          displayCode =
            (resolved?.shift_id && String(resolved.shift_id).trim()) ||
            (resolved?.default_shift &&
              String(resolved.default_shift).trim()) ||
            codeStr;
          color =
            (resolved?.color && String(resolved.color).trim()) ||
            resolveShiftColor(codeStr, "#F0F0F2");
        }
      } else {
        const raw = slotMap.get(iso);
        const hasSlot = raw != null && String(raw).trim() !== "";
        displayCode = hasSlot ? String(raw).trim() : "·";
        color = hasSlot ? resolveShiftColor(displayCode, "#F0F0F2") : "#F0F0F2";
      }

      out.push({
        iso,
        weekday: wdLabels[dow] ?? "",
        dayNum: dom,
        dow,
        displayCode,
        color,
      });
    }
    return out;
  }, [
    bulkPeriodPick?.startDateISO,
    bulkPeriodPick?.endDateISO,
    bulkPeriodTargetNurse?.schedule,
    bulkPeriodTargetNurse?.schedule_ids,
    bulkPeriodCalendar?.year,
    bulkPeriodCalendar?.month,
    bulkSlotResults,
    replacementPeriodMode,
    resolveShiftColor,
    shiftLookupMaps,
  ]);

  /** 추천 API `current_assigned_shift_pk_id` + 코드 → 근무표와 동일한 표시명/색 */
  const getCandidateRowShiftDisplay = useCallback(
    (item: RecommendCandidate) => {
      const pkRaw = item.current_assigned_shift_pk_id;
      const numericId =
        pkRaw != null &&
        String(pkRaw).trim() !== "" &&
        Number.isFinite(Number(pkRaw))
          ? Number(pkRaw)
          : null;
      const resolved = resolveShiftFromScheduleCellFromMaps(
        item.current_assigned_shift_code,
        numericId,
        shiftLookupMaps
      );
      /** 근무표 셀과 동일: 맵의 `shift_id`를 표시(코드 문자열 `E` 등보다 우선) */
      const displayCode =
        (resolved?.shift_id && String(resolved.shift_id).trim()) ||
        (resolved?.default_shift && String(resolved.default_shift).trim()) ||
        (item.current_assigned_shift_code != null &&
        String(item.current_assigned_shift_code).trim() !== ""
          ? String(item.current_assigned_shift_code).trim()
          : "-");
      const colorKey =
        resolved?.shift_id ||
        resolved?.default_shift ||
        item.current_assigned_shift_code ||
        null;
      const badgeColor = resolveShiftColor(
        displayCode === "-" ? null : (colorKey ?? displayCode),
        "#A8A8B3"
      );
      return { displayCode, badgeColor };
    },
    [resolveShiftColor, shiftLookupMaps]
  );

  const canApplyReplacement =
    !!slotPick &&
    !!selectedCandidate &&
    !isRecommendNoCandidateRow(selectedCandidate) &&
    typeof slotPick.dayIndex === "number" &&
    !!shiftLabel;

  /** 기간(BULK): 선택된 시나리오가 `hasApplicableCandidates`일 때만 적용 */
  const canApplyBulkReplacement = useMemo(() => {
    if (!onApplyReplacement || !bulkPeriodPick?.nurseId) return false;
    return selectedBulkPath?.hasApplicableCandidates === true;
  }, [onApplyReplacement, bulkPeriodPick?.nurseId, selectedBulkPath]);

  /** 추천 API 응답 후(로딩 아님) — 다시찾기/설정완료 영역 */
  const showPostSearchFooter = hasRecommendResponse && !isRecommendPending;

  const showRankingScopeFilters = !hasRecommendResponse && !hasBulkResponse;

  return (
    <CustomDragNDropModal
      open={open}
      portalZIndex={portalZIndex}
      initialPosition={stableInitialPosition}
      targetRef={targetRef}
    >
      {({ onMouseDown }) => (
        <section
          className="modal_window pointer-events-auto overflow-hidden rounded-[10px] bg-white shadow-[0_8px_24px_rgba(0,0,0,0.22)]"
          style={{
            minWidth: "368px",
            width: "368px",
            maxWidth: "calc(100vw - 24px)",
            maxHeight: "100%",
            boxSizing: "border-box",
            position: "static",
            top: "auto",
            left: "auto",
            transform: "none",
          }}
        >
          <header
            className="flex justify-center modal_moveHandler p-3 !h-[51px] !bg-sig"
            onMouseDown={onMouseDown}
          >
            <div className="flex items-center ">
              <h3 className="font-pretendard text-xl text-background  ">
                근무자 긴급대체
              </h3>
            </div>

            <button
              className="absolute right-[8px] top-[14px] text-white/70 hover:text-white"
              type="button"
              aria-label="닫기"
              onClick={handleCloseModal}
            >
              <X />
            </button>
          </header>

          <div className=" px-6 py-[20px] !max-w-none font-pretendard">
            <style>{`
              @keyframes emergency-gradient-roll {
                0% { background-position: 0% 50%; }
                50% { background-position: 100% 50%; }
                100% { background-position: 0% 50%; }
              }
            `}</style>
            {showRankingScopeFilters && (
              <div
                className={cn(
                  "mx-auto mb-5 flex w-full flex-col gap-3 border-b border-[#D6D6DE] pb-4 transition-opacity",
                  (isRecommendPending || isBulkRecommendPending) && "opacity-60"
                )}
              >
                <div className="grid w-full grid-cols-2 gap-x-6 gap-y-4">
                  {(
                    [
                      ["ALL", "휴무/근무/휴가 전체"],
                      ["OFF_ONLY", "휴무자 포함"],
                      ["VACATION_ONLY", "휴가자 포함"],
                      ["ON_DUTY_ONLY", "당일 근무자만"],
                    ] as const
                  ).map(([value]) => (
                    <label
                      key={value}
                      className={cn(
                        "flex min-w-0 items-center gap-2",
                        value === "VACATION_ONLY" && "order-2",
                        value === "OFF_ONLY" && "order-3",
                        value === "ON_DUTY_ONLY" && "order-4",
                        isRecommendPending || isBulkRecommendPending
                          ? "cursor-not-allowed"
                          : "cursor-pointer"
                      )}
                    >
                      <Checkbox
                        disabled={isRecommendPending || isBulkRecommendPending}
                        className="data-[state=checked]:bg-[#0E59C6] data-[state=checked]:border-[#0E59C6] focus-visible:ring-[#0E59C6]/50"
                        checked={
                          value === "ALL"
                            ? isAllScopeChecked
                            : rankingScopeChecks.has(value)
                        }
                        onCheckedChange={(checked) => {
                          // 체크박스 UX:
                          // - "전체" 체크 시: 나머지 해제
                          // - 나머지를 모두 체크 시: 자동으로 "전체"(=나머지 해제)
                          if (value === "ALL") {
                            if (checked === true) {
                              setRankingScopeChecks(new Set());
                            }
                            return;
                          }

                          setRankingScopeChecks((prev) => {
                            const next = new Set(prev);
                            if (checked === true) next.add(value);
                            else next.delete(value);

                            if (next.size === NON_ALL_SCOPES.length) {
                              // 전부 선택되면 전체로 축약
                              return new Set();
                            }
                            return next;
                          });
                        }}
                      />
                      <span className="min-w-0 whitespace-nowrap text-[15px] leading-tight text-[#333333]">
                        {value === "ALL"
                          ? "휴무/근무/휴가 전체"
                          : value === "VACATION_ONLY"
                            ? "휴가자 포함"
                            : value === "OFF_ONLY"
                              ? "휴무자 포함"
                              : "당일 근무자만"}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            {(replacementPeriodMode === "sameDay" || !hasBulkResponse) && (
              <div className="mx-auto flex w-full flex-col items-center justify-center gap-3">
                {replacementPeriodMode === "sameDay" ? (
                  slotPick ? (
                    <EmergencySelectionSummaryCard
                      nurseName={slotPick.nurseName}
                      dateText={selectedDateLine ?? ""}
                      shiftLabel={shiftLabel}
                      shiftColor={slotPick.shiftColor ?? "#A8A8B3"}
                    />
                  ) : (
                    <EmergencySelectionSummaryCard
                      nurseName={"근무자"}
                      dateText={"근무표에서 대체할 근무를 선택하세요"}
                      muted
                    />
                  )
                ) : bulkPeriodPick ? (
                  <EmergencySelectionSummaryCard
                    nurseName={bulkPeriodPick.nurseName}
                    dateText={formatEmergencyPeriodLabel(
                      bulkPeriodPick.startDateISO,
                      bulkPeriodPick.endDateISO
                    )}
                  />
                ) : (
                  <EmergencySelectionSummaryCard
                    nurseName={"근무자"}
                    dateText={"시작일 ~ 종료일"}
                    muted
                  />
                )}

                <div className="flex items-center justify-center">
                  <ArrowRight
                    className="size-7 shrink-0 rotate-90 text-[#D6D6DE]"
                    strokeWidth={3}
                    aria-hidden
                  />
                </div>

                <div className="flex w-full flex-col items-stretch">
                  {replacementPeriodMode === "sameDay" ? (
                    <>
                      {/* Single selected-candidate summary card hidden. */}
                      {!hasRecommendResponse && (
                        <EmergencyReplacementFindButton
                          isPending={isRecommendPending}
                          showGradientStyle={
                            isRecommendPending || canRequestRecommendation
                          }
                          disabled={
                            !canRequestRecommendation || isRecommendPending
                          }
                            onClick={() => {
                            setSelectedCandidateId(null);
                            requestRecommend();
                          }}
                        />
                      )}
                    </>
                  ) : (
                    <EmergencyReplacementFindButton
                      isPending={isBulkRecommendPending}
                      showGradientStyle={
                        isBulkRecommendPending || canRequestPeriodRecommendation
                      }
                      disabled={
                        !canRequestPeriodRecommendation ||
                        isBulkRecommendPending
                      }
                      onClick={() => {
                        setSelectedBulkPathRank(null);
                        requestBulkRecommend();
                      }}
                      className="w-full"
                    />
                  )}
                </div>
              </div>
            )}{" "}
            {/* Hide single selected-candidate summary card. */}
            {hasBulkResponse && replacementPeriodMode === "period" && (
              <div className="mx-auto w-full">
                {/* Hide single selected-candidate summary card. */}
                {bulkPeriodPick && bulkPeriodScheduleStrip.length > 0 && (
                  <div className="mb-1">
                    <EmergencySelectionSummaryCard
                      nurseName={bulkPeriodPick.nurseName}
                      dateText={formatEmergencyPeriodLabel(
                        bulkPeriodPick.startDateISO,
                        bulkPeriodPick.endDateISO
                      )}
                    />
                  </div>
                )}
                <div className="mb-2 flex justify-center">
                  <ArrowDown
                    className="size-8 text-zinc-300"
                    strokeWidth={2.5}
                    aria-hidden
                  />
                </div>
                {bulkPaths.length === 0 && bulkSlotResults.length === 0 ? (
                  <p className="py-6 text-center text-[14px] text-zinc-500">
                    추천 결과가 없습니다.
                  </p>
                ) : (
                  <>
                    {bulkPaths.length > 0 && (
                      <div className="mb-3 flex w-full flex-col gap-[9px]">
                        {bulkPaths.map((path, pathIdx) => {
                          const selected =
                            selectedBulkPathRank === path.path_rank;
                          const vCount = countBulkPathViolations(
                            path.violations
                          );
                          const title =
                            path.scenario_label?.trim() ||
                            `시나리오 ${path.path_rank}`;
                          const pathDisabled = !path.hasApplicableCandidates;
                          /** 비활성 카드는 hoverWash 제거 — 호버 시 색이 바뀌지 않게 */
                          const frameTone = selected
                            ? "gradientFill"
                            : pathDisabled
                              ? "solidWhite"
                              : "hoverWash";
                          return (
                            <ReplacementGradientFrame
                              key={`bulk-path-${pathIdx}-${path.path_rank}`}
                              variant="scenario"
                              tone={frameTone}
                              className={cn(
                                pathDisabled && "opacity-30 grayscale-30"
                              )}
                            >
                              <button
                                type="button"
                                  onClick={() => {
                                  if (pathDisabled) return;
                                  setSelectedBulkPathRank(
                                    selected ? null : path.path_rank
                                  );
                                }}
                                className={cn(
                                  "relative flex min-h-0 w-full flex-1 justify-between bg-transparent px-4 py-3.5 text-left transition-colors duration-200",
                                  pathDisabled
                                    ? /* 전역 `button { cursor: pointer }` 덮어씀 */
                                      "!cursor-not-allowed text-foreground"
                                    : "cursor-pointer",
                                  !pathDisabled &&
                                    (selected
                                      ? "text-background"
                                      : "text-foreground group-hover:text-background")
                                )}
                              >
                                <span
                                  className={cn(
                                    "text-[14px] font-semibold leading-snug text-center transition-colors duration-200",
                                    pathDisabled
                                      ? "text-foreground"
                                      : selected
                                        ? "text-background"
                                        : "text-foreground group-hover:text-background"
                                  )}
                                >
                                  {title}
                                </span>
                                <span
                                  className={cn(
                                    "text-center text-sm transition-colors duration-200",
                                    pathDisabled &&
                                      "block min-w-0 max-w-full truncate px-0.5",
                                    pathDisabled
                                      ? "text-secondary-foreground"
                                      : selected
                                        ? "text-background/90"
                                        : "text-muted-foreground group-hover:text-background/90"
                                  )}
                                  title={
                                    pathDisabled
                                      ? "조건에 맞는 후보가 없습니다"
                                      : undefined
                                  }
                                >
                                  {pathDisabled
                                    ? "조건에 맞는 후보가 없습니다"
                                    : `대체자 ${path.steps.length}명 | 위반사항 ${vCount}개`}
                                </span>
                              </button>
                            </ReplacementGradientFrame>
                          );
                        })}
                      </div>
                    )}

                    {selectedBulkPath?.steps?.length ? (
                      <div className="scrollbar-float mb-4 max-h-[240px] overflow-y-auto rounded-[4px] border border-[#D6D6DE]">
                        <table className="w-full table-fixed border-collapse text-[12px]">
                          <thead className="sticky top-0 z-[1] bg-[#F0F0F2] text-[#333333]">
                            <tr className="h-9">
                              <th className="w-[54px] border-r border-[#D6D6DE] text-center font-semibold">
                                날짜
                              </th>
                              <th className="w-[86px] border-r border-[#D6D6DE] text-center font-semibold">
                                {bulkPeriodPick?.nurseName
                                  ? `${bulkPeriodPick.nurseName} 근무`
                                  : "근무"}
                              </th>
                              <th className="bg-[#F2F2FF] text-center font-semibold">
                                {`대체자 총 ${selectedBulkPath.steps.length}명 / 근무`}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedBulkPath.steps.map((step) => {
                              const date = DateUtils.parseKoreanDate(
                                step.date.slice(0, 10)
                              );
                              const targetCode = String(
                                step.shift ?? ""
                              ).trim();
                              const candidateCode = String(
                                step.candidate.current_assigned_shift_code ?? ""
                              ).trim();
                              return (
                                <tr
                                  key={`${step.date}-${step.shift}-${step.candidate.nurse_id}`}
                                  className="h-10 border-t border-[#D6D6DE]"
                                >
                                  <td className="w-[54px] border-r border-[#D6D6DE] text-center font-medium text-[#333333]">
                                    {date.isValid()
                                      ? `${date.date()}일`
                                      : step.date.slice(8, 10)}
                                  </td>
                                  <td className="w-[86px] border-r border-[#D6D6DE] text-center">
                                    <span
                                      className="inline-flex size-7 items-center justify-center rounded-[5px] font-bold text-white"
                                      style={{
                                        backgroundColor: resolveShiftColor(
                                          targetCode,
                                          "#A8A8B3"
                                        ),
                                        color: getReadableShiftCodeTextColor(
                                          resolveShiftColor(targetCode, "#A8A8B3")
                                        ),
                                        fontSize:
                                          getScheduleShiftCodeFontSize(
                                            targetCode
                                          ),
                                      }}
                                    >
                                      {targetCode || "-"}
                                    </span>
                                  </td>
                                  <td className="bg-[#F2F2FF] px-2 text-center font-medium text-[#333333]">
                                    <div className="flex min-w-0 items-center justify-center gap-2">
                                      <span className="min-w-0 truncate">
                                        {step.candidate.name || "-"}
                                      </span>
                                      <span
                                        className="inline-flex size-7 shrink-0 items-center justify-center rounded-[5px] font-bold text-white"
                                        style={{
                                          backgroundColor: resolveShiftColor(
                                            candidateCode,
                                            "#A8A8B3"
                                          ),
                                          color: getReadableShiftCodeTextColor(
                                            resolveShiftColor(candidateCode, "#A8A8B3")
                                          ),
                                          fontSize:
                                            getScheduleShiftCodeFontSize(
                                              candidateCode || "-"
                                            ),
                                        }}
                                      >
                                        {candidateCode || "-"}
                                      </span>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : null}

                    <div className="mt-7 flex justify-center gap-3 pb-2">
                      <div className="grid grid-cols-2 flex-1 gap-2">
                        <BtnCancel
                          type="button"
                          className="w-full"
                          onClick={() => {
                            resetBulkRecommendMutation();
                            setSelectedBulkPathRank(null);
                            onBulkResultChange?.(false);
                          }}
                        >
                          다시 찾기
                        </BtnCancel>
                        <BtnSuccess
                          type="button"
                          className="w-full"
                          disabled={!canApplyBulkReplacement}
                          title={
                            canApplyBulkReplacement
                              ? undefined
                              : "적용할 수 없습니다."
                          }
                          onClick={() => {
                            if (
                              !canApplyBulkReplacement ||
                              !onApplyReplacement ||
                              !bulkPeriodPick ||
                              !selectedBulkPath?.steps?.length
                            ) {
                              return;
                            }
                            const targetNurseId = String(bulkPeriodPick.nurseId);
                            for (const step of selectedBulkPath.steps) {
                              const dayIndex = bulkDateISOToDayIndex(step.date);
                              if (dayIndex == null) continue;
                              const rid = step.candidate?.nurse_id;
                              if (rid == null) continue;
                              onApplyReplacement({
                                nurseId: String(rid),
                                targetNurseId,
                                dayIndex,
                                shiftCode: String(step.shift).trim(),
                              });
                            }
                            toast.success(
                              "선택한 시나리오가 근무표에 반영되었습니다."
                            );
                            resetEmergencyModalState();
                            onOpenChange(false);
                          }}
                        >
                          적용
                        </BtnSuccess>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
            {hasRecommendResponse && replacementPeriodMode === "sameDay" && (
              <div className="mx-auto  px-0">
                {sortedRecommendList.length === 0 ? (
                  <p className="text-center text-[14px] text-zinc-500">
                    추천 결과가 없습니다.
                  </p>
                ) : (
                  <div className="scrollbar-float max-h-[280px] w-full overflow-x-auto overflow-y-auto rounded-[4px] border border-[#D6D6DE]">
                    <table className="   border-collapse text-[13px] w-full">
                      <thead className="sticky top-0 z-[1] border-b bg-[#F0F0F2] border-b-[#D6D6DE]">
                        <tr>
                          <th className="min-w-[40px] whitespace-nowrap border-b border-[#E6E6ED] px-1 py-2.5 text-center font-semibold text-zinc-700">
                            No.
                          </th>
                          <th className=" border-b border-[#E6E6ED] text-center py-2.5  font-semibold text-zinc-700">
                            이름
                          </th>
                          <th className="min-w-[80px] whitespace-nowrap border-b border-[#E6E6ED] py-2.5 text-center font-semibold text-zinc-700">
                            현재 근무
                          </th>
                          <th className="min-w-[220px] border-b border-[#E6E6ED] px-3 py-2.5 text-left font-semibold text-zinc-700">
                            추천태그
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedRecommendList.map((item, idx) => {
                          const nid = String(item.nurse_id);
                          const rankNo =
                            typeof item.rank === "number" &&
                            Number.isFinite(item.rank)
                              ? item.rank
                              : idx + 1;
                          const { displayCode: code, badgeColor } =
                            getCandidateRowShiftDisplay(item);
                          const tags = sortRecommendTagsForDisplay(
                            normalizeRecommendTags(item.tags)
                          );
                          const isSelected = selectedCandidateId === nid;
                          return (
                            <tr
                              key={`recommend-${nid}-${idx}`}
                              className={cn(
                                "cursor-pointer border-b border-[#F1F1F5] transition-colors hover:bg-zinc-50/80",
                                isSelected && "!bg-[#D4F0FF]"
                              )}
                              onClick={() => setSelectedCandidateId(nid)}
                            >
                              <td className="py-1.5 text-center text-zinc-600 tabular-nums">
                                {rankNo}
                              </td>
                              <td
                                className={cn(
                                  "max-w-[200px] truncate px-2 py-1.5 text-center text-zinc-900 underline-offset-2 hover:underline",
                                  isSelected ? "font-semibold" : "font-medium"
                                )}
                              >
                                {item.name}
                              </td>
                              <td className=" py-1.5 text-center">
                                <span
                                  className="inline-flex size-8  items-center justify-center rounded-sm px-1.5 py-1 font-bold text-white shadow-sm"
                                  style={{
                                    backgroundColor: badgeColor,
                                    color: getReadableShiftCodeTextColor(badgeColor),
                                    fontSize:
                                      code === "-"
                                        ? 12
                                        : getScheduleShiftCodeFontSize(code),
                                  }}
                                >
                                  <span className="truncate text-sm">
                                    {code}
                                  </span>
                                </span>
                              </td>
                              <td className=" py-2 align-center pr-5 ">
                                <div className="">
                                  {tags.length === 0 ? (
                                    <span className="text-zinc-400">-</span>
                                  ) : (
                                    <div className="flex w-max flex-nowrap items-center gap-2 text-xs leading-snug">
                                      {tags.map((t, idx) => {
                                        const ui = getRecommendTagUi(t);
                                        return (
                                          <div
                                            key={`${t}-${idx}`}
                                            className="flex items-start justify-center"
                                          >
                                            <span
                                              className={recommendTagInlineCn(
                                                t,
                                                isSelected
                                              )}
                                            >
                                              {ui.label}
                                            </span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
            {showPostSearchFooter && (
              <div className="mt-7 flex justify-center gap-3 pb-2">
                <div className="grid grid-cols-2 flex-1 gap-2">
                  <BtnCancel
                    type="button"
                    className="w-full"
                    onClick={() => {
                      resetEmergencyModalState();
                    }}
                  >
                    다시찾기
                  </BtnCancel>
                  <BtnSuccess
                    type="button"
                    disabled={!canApplyReplacement}
                    onClick={() => {
                      if (
                        !slotPick ||
                        !selectedCandidate ||
                        !onApplyReplacement
                      ) {
                        return;
                      }
                      onApplyReplacement({
                        nurseId: String(selectedCandidate.nurse_id),
                        targetNurseId: String(slotPick.nurseId),
                        dayIndex: slotPick.dayIndex,
                        shiftCode: slotPick.shiftCode,
                      });
                      toast.success("근무자가 대체되었습니다.");
                      resetEmergencyModalState();
                      onOpenChange(false);
                    }}
                  >
                    설정완료
                  </BtnSuccess>
                </div>
              </div>
            )}
          </div>
        </section>
      )}
    </CustomDragNDropModal>
  );
}
